import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS } from "../constants/frameworks";
import { motion, AnimatePresence } from "framer-motion";
import { CardGridSkeleton } from "../components/skeletons";
import {
  Search, Filter, ChevronRight, Shield, BookOpen, ExternalLink,
  CheckCircle2, XCircle, Info
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { PageHeader } from "../components/PageHeader";

const ControlLibrary = () => {
  const { token } = useAuth();
  const { t, locale } = useI18n();
  const { activeFrameworks } = useOrg();
  const [controls, setControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFramework, setSelectedFramework] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedControl, setSelectedControl] = useState(null);
  const [frameworkSummary, setFrameworkSummary] = useState([]);

  useEffect(() => {
    fetchControls();
    fetchFrameworkSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedFramework, locale]);

  useEffect(() => {
    filterControls();
  }, [controls, searchQuery, selectedFramework, selectedCategory]);

  const fetchControls = async () => {
    setLoading(true);
    try {
      // When a specific framework is picked we query the backend with that
      // filter so Zona Franca seeds (oea/cnzfe/iso22000/bpm/haccp/nordom477)
      // — which live in routes/zona_franca.py, not db.controls — are returned
      // through the unified /controls adapter.
      if (selectedFramework && selectedFramework !== "all") {
        const response = await axios.get(`${API}/controls`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 500, framework: selectedFramework, locale }
        });
        setControls(response.data);
      } else {
        // "All": load IT framework controls from db.controls + ZF seeds in parallel
        const ZF_CODES = ["oea", "cnzfe", "iso22000", "bpm", "haccp", "nordom477"];
        const itReq = axios.get(`${API}/controls`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 500, locale }
        });
        const zfReqs = ZF_CODES.map((fw) =>
          axios.get(`${API}/controls`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { limit: 500, framework: fw, locale }
          }).then((r) => r.data).catch(() => [])
        );
        const [itRes, ...zfResults] = await Promise.all([itReq, ...zfReqs]);
        setControls([...(itRes.data || []), ...zfResults.flat()]);
      }
    } catch (error) {
      console.error("Failed to fetch controls:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFrameworkSummary = async () => {
    try {
      const response = await axios.get(`${API}/controls/frameworks/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrameworkSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch framework summary:", error);
    }
  };

  const filterControls = () => {
    let filtered = [...controls];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.control_number.toLowerCase().includes(query)
      );
    }

    if (selectedFramework !== "all") {
      filtered = filtered.filter(c => c.framework === selectedFramework);
    }

    if (selectedCategory !== "all") {
      filtered = filtered.filter(c => c.category === selectedCategory);
    }

    setFilteredControls(filtered);
  };

  const getFrameworkBadgeClass = (framework) => {
    switch (framework) {
      case "iso27001": return "badge-iso27001";
      case "nist_scf": return "badge-nist";
      case "cis_v8": return "badge-cis";
      case "iso20000": return "badge-iso20000";
      default: return "";
    }
  };

  const getFrameworkLabel = (framework) => {
    const fw = FRAMEWORKS.find((f) => f.id === framework);
    return fw ? fw.short : framework;
  };

  const categories = [...new Set(controls.map(c => c.category))].filter(Boolean);

  if (loading) {
    return <CardGridSkeleton count={9} label={t("loading")} />;
  }

  return (
    <div className="p-6 space-y-6" data-testid="control-library">
      <PageHeader
        title={t("ctrl.list_title")}
        description={t("ctrl.list_subtitle")}
      />

      {/* Framework Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {FRAMEWORKS
          .filter(fw => activeFrameworks.includes(fw.id))
          .map(fw => ({ key: fw.id, label: fw.short, color: fw.color }))
          .map(fw => {
          const count = frameworkSummary.find(f => f.framework === fw.key)?.count || 0;
          return (
            <Card
              key={fw.key}
              className={`cursor-pointer transition-all duration-base ease-in-out hover:-translate-y-0.5 hover:shadow-md hover:border-primary ${selectedFramework === fw.key ? 'ring-2 ring-primary border-primary' : ''}`}
              onClick={() => setSelectedFramework(selectedFramework === fw.key ? "all" : fw.key)}
              data-testid={`framework-filter-${fw.key}`}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fw.color }} />
                  <div>
                    <p className="text-sm font-medium">{fw.label}</p>
                    <p className="text-2xl font-heading font-bold">{count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("search") + "..."}
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="control-search-input"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
            <SelectValue placeholder={t("category")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t("ux.showing_of_controls").replace("{x}", filteredControls.length).replace("{y}", controls.length)}
        </p>
        {(selectedFramework !== "all" || selectedCategory !== "all" || searchQuery) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedFramework("all");
              setSelectedCategory("all");
              setSearchQuery("");
            }}
            data-testid="clear-filters-btn"
          >
            {t("ux.clear_filters")}
          </Button>
        )}
      </div>

      {/* Controls List */}
      <ScrollArea className="h-[calc(100vh-24rem)]">
        <div className="space-y-3 pr-4">
          <AnimatePresence mode="popLayout">
            {filteredControls.map((control, index) => (
              <motion.div
                key={control.control_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
              >
                <Card
                  className="cursor-pointer transition-all duration-base ease-in-out hover:-translate-y-0.5 hover:shadow-md hover:border-primary"
                  onClick={() => setSelectedControl(control)}
                  data-testid={`control-card-${control.control_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className={getFrameworkBadgeClass(control.framework)}>
                            {getFrameworkLabel(control.framework)}
                          </Badge>
                          <span className="text-sm font-mono text-muted-foreground">
                            {control.control_number}
                          </span>
                          {control.category && (
                            <Badge variant="secondary" className="text-xs">
                              {control.category}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-sm mb-1 truncate">
                          {control.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {control.description}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredControls.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No controls found matching your criteria</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Control Detail Dialog */}
      <Dialog open={!!selectedControl} onOpenChange={() => setSelectedControl(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedControl && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className={getFrameworkBadgeClass(selectedControl.framework)}>
                    {getFrameworkLabel(selectedControl.framework)}
                  </Badge>
                  <span className="text-sm font-mono text-muted-foreground">
                    {selectedControl.control_number}
                  </span>
                </div>
                <DialogTitle className="text-xl">{selectedControl.title}</DialogTitle>
                <DialogDescription>
                  {selectedControl.domain && `${selectedControl.domain} • `}
                  {selectedControl.category}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Description
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {selectedControl.description}
                  </p>
                </div>

                {selectedControl.implementation_guidance && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Implementation Guidance
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {selectedControl.implementation_guidance}
                    </p>
                  </div>
                )}

                {selectedControl.evidence_requirements?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Evidence Requirements
                    </h4>
                    <ul className="space-y-1">
                      {selectedControl.evidence_requirements.map((req, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ControlLibrary;
